import { useCurrentUserPagePermissions } from './usePagePermissions'
import { useEnforcement } from '../../hooks/useEnforcement'
import { useTenantAccess } from '../../hooks/useTenantAccess'
import { hasAnyAccessInSection } from './pageRegistry'

/**
 * Single source of truth for sub-nav tab visibility inside section layouts
 * (Sell, Buy, Trading, Finance, Payroll, System, …). Mirrors the precedence
 * used by <PermissionGuard> and the Sidebar exactly so a tab is only shown
 * when the page behind it is actually reachable:
 *
 *   1. super_admin → always visible (platform role bypasses everything)
 *   2. plan gate (when enforced) → hides the tab for EVERYONE else, owner
 *      included; held while plan pages load so we don't flash an empty strip
 *   3. owner, or a role with no page permissions seeded yet → role gate
 *      bypassed (the plan gate above still applied)
 *   4. staff → the role's level for the page must not be 'none'
 */
export function usePageVisibility() {
  const pagePerms = useCurrentUserPagePermissions()
  const { enforced } = useEnforcement()
  const { hasPage, loadingPages } = useTenantAccess()

  const hasRolePerms = pagePerms.levels.size > 0
  const ownerBypass = pagePerms.isOwner || !hasRolePerms
  const planEnforced = enforced && !loadingPages

  function isPageVisible(pageKey: string): boolean {
    if (pagePerms.isSuperAdmin) return true
    if (planEnforced && !hasPage(pageKey)) return false
    if (ownerBypass) return true
    return pagePerms.getLevel(pageKey) !== 'none'
  }

  function isSectionVisible(module: string, section: string): boolean {
    if (pagePerms.isSuperAdmin) return true
    return hasAnyAccessInSection(module, section, pagePerms.getLevel, {
      enforced: planEnforced,
      hasPage,
      ownerBypass,
    })
  }

  return {
    isPageVisible,
    isSectionVisible,
    isLoading: pagePerms.isLoading,
  }
}
